import { useState } from 'react';
import { useSelector } from 'react-redux';
import { FaChartBar, FaChartLine, FaChartPie, FaCalendarAlt, FaBook, FaExclamationTriangle } from 'react-icons/fa';

const AdvancedAnalytics = () => {
  const [activeTab, setActiveTab] = useState('heatmap');
  const { darkMode } = useSelector((state) => state.ui);
  
  // Sample data for demonstration
  const heatmapData = [
    { day: 'Monday', '8AM': 15, '10AM': 25, '12PM': 18, '2PM': 30, '4PM': 22, '6PM': 35, '8PM': 40 },
    { day: 'Tuesday', '8AM': 20, '10AM': 30, '12PM': 15, '2PM': 25, '4PM': 18, '6PM': 40, '8PM': 35 },
    { day: 'Wednesday', '8AM': 25, '10AM': 35, '12PM': 20, '2PM': 15, '4PM': 30, '6PM': 45, '8PM': 30 },
    { day: 'Thursday', '8AM': 30, '10AM': 20, '12PM': 25, '2PM': 35, '4PM': 40, '6PM': 30, '8PM': 25 },
    { day: 'Friday', '8AM': 15, '10AM': 25, '12PM': 30, '2PM': 20, '4PM': 35, '6PM': 25, '8PM': 20 },
    { day: 'Saturday', '8AM': 10, '10AM': 15, '12PM': 25, '2PM': 40, '4PM': 45, '6PM': 50, '8PM': 45 },
    { day: 'Sunday', '8AM': 5, '10AM': 10, '12PM': 20, '2PM': 30, '4PM': 40, '6PM': 45, '8PM': 35 },
  ];
  
  const skillGapData = [
    { skill: 'JavaScript', proficiency: 75, targetProficiency: 90 },
    { skill: 'React', proficiency: 60, targetProficiency: 85 },
    { skill: 'Node.js', proficiency: 40, targetProficiency: 80 },
    { skill: 'CSS/SCSS', proficiency: 80, targetProficiency: 85 },
    { skill: 'Database Design', proficiency: 50, targetProficiency: 75 },
    { skill: 'API Development', proficiency: 65, targetProficiency: 80 },
  ];
  
  const predictiveData = [
    { course: 'JavaScript Fundamentals', completionProbability: 95, riskLevel: 'low' },
    { course: 'Advanced React Patterns', completionProbability: 75, riskLevel: 'medium' },
    { course: 'Node.js Backend Development', completionProbability: 60, riskLevel: 'medium' },
    { course: 'Database Design & SQL', completionProbability: 40, riskLevel: 'high' },
    { course: 'DevOps for Developers', completionProbability: 30, riskLevel: 'high' },
  ];
  
  const engagementData = [
    { metric: 'Video Watch Time', value: '45 hours', change: '+15%', trend: 'up' },
    { metric: 'Discussion Posts', value: '28 posts', change: '+8%', trend: 'up' },
    { metric: 'Quiz Attempts', value: '42 attempts', change: '-5%', trend: 'down' },
    { metric: 'Assignment Submissions', value: '12 submissions', change: '+20%', trend: 'up' },
    { metric: 'Peer Reviews', value: '8 reviews', change: '+100%', trend: 'up' },
  ];

  // Helper function to determine cell color based on value
  const getCellColor = (value) => {
    if (value < 15) return 'bg-blue-100';
    if (value < 25) return 'bg-blue-200';
    if (value < 35) return 'bg-blue-300';
    if (value < 45) return 'bg-blue-400';
    return 'bg-blue-500 text-white';
  };

  // Helper function to determine risk badge color
  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to determine trend icon and color
  const getTrendIndicator = (trend) => {
    if (trend === 'up') {
      return <span className="text-green-500">↑</span>;
    } else {
      return <span className="text-red-500">↓</span>;
    }
  };

  return (
    <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} container-rounded-lg shadow-md overflow-hidden`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-b'} px-6 py-4`}>
        <h2 className="text-lg font-medium">Advanced Analytics & Insights</h2>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mt-1`}>Detailed metrics to optimize your learning journey</p>
      </div>

      {/* Tabs */}
      <div className={`${darkMode ? 'border-gray-700' : 'border-b'}`}>
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('heatmap')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'heatmap' 
              ? 'border-b-2 border-primary text-primary' 
              : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaCalendarAlt className="inline mr-2" />
            Learning Heat Map
          </button>
          <button
            onClick={() => setActiveTab('skillgap')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'skillgap' 
              ? 'border-b-2 border-primary text-primary' 
              : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaChartBar className="inline mr-2" />
            Skill Gap Analysis
          </button>
          <button
            onClick={() => setActiveTab('predictive')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'predictive' 
              ? 'border-b-2 border-primary text-primary' 
              : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaChartLine className="inline mr-2" />
            Predictive Analytics
          </button>
          <button
            onClick={() => setActiveTab('engagement')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'engagement' 
              ? 'border-b-2 border-primary text-primary' 
              : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaChartPie className="inline mr-2" />
            Engagement Metrics
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Learning Heat Map */}
        {activeTab === 'heatmap' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Weekly Learning Activity</h3>
              <div className="text-sm text-gray-500">
                <span className="inline-block w-3 h-3 bg-blue-100 mr-1"></span> Low
                <span className="inline-block w-3 h-3 bg-blue-300 mx-1"></span> Medium
                <span className="inline-block w-3 h-3 bg-blue-500 ml-1"></span> High
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                    {Object.keys(heatmapData[0]).filter(key => key !== 'day').map(time => (
                      <th key={time} className="py-2 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {time}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapData.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-3 text-sm font-medium text-gray-900">{row.day}</td>
                      {Object.entries(row).filter(([key]) => key !== 'day').map(([time, value], cellIndex) => (
                        <td key={cellIndex} className="py-2 px-3 text-center">
                          <span className={`inline-block w-full py-2 rounded-md text-xs font-medium ${getCellColor(value)}`}>
                            {value}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">Values represent minutes spent learning during each time period</p>
          </div>
        )}

        {/* Skill Gap Analysis */}
        {activeTab === 'skillgap' && (
          <div>
            <h3 className="font-medium mb-4">Your Skills vs. Target Proficiency</h3>
            <div className="space-y-6">
              {skillGapData.map((skill, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{skill.skill}</span>
                    <span className="text-sm text-gray-500">{skill.proficiency}% / {skill.targetProficiency}%</span>
                  </div>
                  <div className="relative pt-1">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                      <div 
                        style={{ width: `${skill.proficiency}%` }} 
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
                      ></div>
                    </div>
                    <div 
                      style={{ left: `${skill.targetProficiency}%` }} 
                      className="absolute top-0 w-0.5 h-4 bg-gray-500 transform -translate-x-1/2"
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {skill.targetProficiency - skill.proficiency > 20 ? (
                      <span className="text-red-500">Significant gap - prioritize this skill</span>
                    ) : skill.targetProficiency - skill.proficiency > 10 ? (
                      <span className="text-yellow-500">Moderate gap - continue improving</span>
                    ) : (
                      <span className="text-green-500">On track - maintain proficiency</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 container-rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Recommended Focus Areas</h4>
              <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
                <li>Node.js - Take the "Node.js Advanced Concepts" course</li>
                <li>Database Design - Complete the SQL practice exercises</li>
                <li>React - Work on the component optimization project</li>
              </ul>
            </div>
          </div>
        )}

        {/* Predictive Analytics */}
        {activeTab === 'predictive' && (
          <div>
            <h3 className="font-medium mb-4">Course Completion Forecast</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion Probability</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {predictiveData.map((course, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <FaBook className="text-gray-400 mr-2" />
                          {course.course}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${course.completionProbability > 70 ? 'bg-green-500' : course.completionProbability > 50 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                            style={{ width: `${course.completionProbability}%` }}
                          ></div>
                        </div>
                        <span className="text-xs mt-1 inline-block">{course.completionProbability}%</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs rounded-full ${getRiskBadgeColor(course.riskLevel)}`}>
                          {course.riskLevel.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {course.riskLevel === 'high' ? (
                          <button className="text-red-600 hover:text-red-800 flex items-center">
                            <FaExclamationTriangle className="mr-1" /> Intervention Needed
                          </button>
                        ) : course.riskLevel === 'medium' ? (
                          <button className="text-yellow-600 hover:text-yellow-800">Schedule Check-in</button>
                        ) : (
                          <span className="text-green-600">On Track</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 p-4 bg-yellow-50 container-rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">At-Risk Learner Alert</h4>
              <p className="text-sm text-yellow-700">You have 2 courses with high dropout risk. Consider scheduling a session with a learning advisor to create an intervention plan.</p>
            </div>
          </div>
        )}

        {/* Engagement Metrics */}
        {activeTab === 'engagement' && (
          <div>
            <h3 className="font-medium mb-4">Your Learning Engagement</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {engagementData.map((metric, index) => (
                <div key={index} className="bg-gray-50 container-rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">{metric.metric}</p>
                      <p className="text-xl font-bold mt-1">{metric.value}</p>
                    </div>
                    <div className={`text-sm ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                      {getTrendIndicator(metric.trend)} {metric.change}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div 
                        className={`h-2 rounded-full ${metric.trend === 'up' ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.abs(parseInt(metric.change))}0%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <h4 className="font-medium mb-3">Engagement Insights</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  <span>Your video watch time has increased significantly, showing strong engagement with course content.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Quiz attempts have decreased. Consider scheduling regular practice sessions to reinforce learning.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Your participation in peer reviews has doubled, demonstrating excellent community engagement.</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedAnalytics;